import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

const ROLE_ORDER = { admin: 0, gestor: 1, coordenador: 2 }
const ROLE_LABEL = { admin: 'Admin', gestor: 'Gestor', coordenador: 'Coordenador' }
const ROLE_BADGE = { admin: 'badge-blue', gestor: 'badge-gray', coordenador: 'badge-amber' }

export default function Gestores() {
  const { isAdmin, isSuperAdmin, selectedCompany, profile } = useAuth()
  const navigate = useNavigate()
  const [usuarios, setUsuarios] = useState([])
  const [indicadoresPorGestor, setIndicadoresPorGestor] = useState({})
  const [vinculos, setVinculos] = useState([]) // rows de gestor_coordenador
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ nome: '', cargo: '', whatsapp: '', email: '', senha: '', role: 'gestor' })
  const [editando, setEditando] = useState(null)
  const [editForm, setEditForm] = useState({ nome: '', cargo: '', whatsapp: '', role: 'gestor' })
  const [confirmandoExcluir, setConfirmandoExcluir] = useState(null)
  const [novoGestorCoords, setNovoGestorCoords] = useState(new Set())
  const [editGestorCoords, setEditGestorCoords] = useState(new Set())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [sucesso, setSucesso] = useState('')
  const [alterandoSenha, setAlterandoSenha] = useState(null)
  const [novaSenha, setNovaSenha] = useState('')

  useEffect(() => {
    if (!isAdmin && !isSuperAdmin) { navigate('/'); return }
    fetchAll()
  }, [isAdmin, isSuperAdmin, selectedCompany])

  async function fetchAll() {
    setLoading(true)
    let profsQ = supabase.from('profiles').select('*').order('nome')
    let indsQ = supabase.from('indicadores').select('id, nome, gestor_id')
    let gcQ = supabase.from('gestor_coordenador').select('*')
    if (isSuperAdmin && selectedCompany) {
      profsQ = profsQ.eq('company_id', selectedCompany.id)
      indsQ = indsQ.eq('company_id', selectedCompany.id)
      gcQ = gcQ.eq('company_id', selectedCompany.id)
    }
    const [{ data: profs }, { data: inds }, { data: gc }] = await Promise.all([profsQ, indsQ, gcQ])
    const sorted = (profs || []).sort((a, b) => {
      const diff = (ROLE_ORDER[a.role] ?? 3) - (ROLE_ORDER[b.role] ?? 3)
      return diff !== 0 ? diff : a.nome.localeCompare(b.nome, 'pt-BR')
    })
    setUsuarios(sorted)
    const mapa = {}
    inds?.forEach(ind => {
      if (!mapa[ind.gestor_id]) mapa[ind.gestor_id] = []
      mapa[ind.gestor_id].push(ind)
    })
    setIndicadoresPorGestor(mapa)
    setVinculos(gc || [])
    setLoading(false)
  }

  async function handleCreate(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSucesso('')

    const companyId = isSuperAdmin ? selectedCompany?.id : profile?.company_id
    if (!companyId) {
      setError(isSuperAdmin ? 'Selecione uma empresa no menu lateral antes de criar um usuário.' : 'Empresa não identificada.')
      setSaving(false)
      return
    }

    // Criação validada no servidor (Edge Function). O signup público está
    // desligado; quem cria é o admin autenticado, e a função confere o papel
    // e a empresa antes de usar a service key.
    const { data, error: fnError } = await supabase.functions.invoke('criar-usuario', {
      body: {
        nome: form.nome,
        email: form.email,
        senha: form.senha,
        role: form.role,
        cargo: form.cargo || null,
        whatsapp: form.whatsapp || null,
        company_id: companyId,
        coordenadores: form.role === 'gestor' ? [...novoGestorCoords] : [],
      },
    })

    // Erro HTTP (!=2xx) chega em fnError com a resposta em fnError.context.
    if (fnError) {
      let msg = 'Falha ao criar usuário.'
      try {
        const body = await fnError.context.json()
        if (body?.error) msg = body.error
      } catch { /* sem corpo JSON */ }
      setError(msg)
      setSaving(false)
      return
    }
    if (data?.error) {
      setError(data.error)
      setSaving(false)
      return
    }

    setSaving(false)
    setSucesso(`Usuário ${form.nome} criado com sucesso!`)
    setShowForm(false)
    setForm({ nome: '', cargo: '', whatsapp: '', email: '', senha: '', role: 'gestor' })
    setNovoGestorCoords(new Set())
    await fetchAll()
    setTimeout(() => setSucesso(''), 4000)
  }

  function handleOpenEdit(usuario) {
    setEditando(usuario)
    setEditForm({ nome: usuario.nome || '', cargo: usuario.cargo || '', whatsapp: usuario.whatsapp || '', role: usuario.role || 'gestor' })
    const atuais = new Set(vinculos.filter(v => v.gestor_id === usuario.id).map(v => v.coordenador_id))
    setEditGestorCoords(atuais)
    setError('')
  }

  async function handleEditSave(e) {
    e.preventDefault()
    setSaving(true)
    setError('')

    const { error: err } = await supabase.from('profiles').update({
      nome: editForm.nome,
      cargo: editForm.cargo || null,
      whatsapp: editForm.whatsapp || null,
      role: editForm.role,
    }).eq('id', editando.id)

    if (err) {
      setError(err.message)
      setSaving(false)
      return
    }

    if (editForm.role === 'gestor') {
      const atuais = new Set(vinculos.filter(v => v.gestor_id === editando.id).map(v => v.coordenador_id))
      const paraAdicionar = [...editGestorCoords].filter(id => !atuais.has(id))
      const paraRemover = [...atuais].filter(id => !editGestorCoords.has(id))
      const ops = []
      if (paraAdicionar.length > 0)
        ops.push(supabase.from('gestor_coordenador').insert(paraAdicionar.map(coordenador_id => ({ gestor_id: editando.id, coordenador_id }))))
      for (const coordenador_id of paraRemover)
        ops.push(supabase.from('gestor_coordenador').delete().eq('gestor_id', editando.id).eq('coordenador_id', coordenador_id))
      await Promise.all(ops)
    }

    setSaving(false)
    setEditando(null)
    setSucesso(`Usuário ${editForm.nome} atualizado.`)
    await fetchAll()
    setTimeout(() => setSucesso(''), 4000)
  }

  async function handleDelete(usuario) {
    // O RPC remove o auth.user, e profiles.id tem ON DELETE CASCADE — o
    // profile sai junto. Apagar o profile antes deixaria o RPC sem o registro
    // que ele usa para conferir se o alvo é da mesma empresa do admin.
    setError('')
    const { error: err } = await supabase.rpc('delete_auth_user', { user_id: usuario.id })
    setConfirmandoExcluir(null)
    if (err) { setError(err.message); return }
    setUsuarios(prev => prev.filter(u => u.id !== usuario.id))
    setSucesso(`Usuário ${usuario.nome} removido.`)
    setTimeout(() => setSucesso(''), 4000)
  }

  async function handleAlterarSenha(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    const { error: err } = await supabase.rpc('update_user_password', {
      user_id: alterandoSenha.id,
      new_password: novaSenha,
    })
    setSaving(false)
    if (err) { setError(err.message); return }
    setAlterandoSenha(null)
    setNovaSenha('')
    setSucesso(`Senha de ${alterandoSenha.nome} alterada com sucesso.`)
    setTimeout(() => setSucesso(''), 4000)
  }

  const coordenadoresList = usuarios.filter(u => u.role === 'coordenador')

  function roleInfoBox(role) {
    if (role === 'admin') return {
      bg: 'var(--blue-bg)', border: 'var(--blue-border)', color: '#1e40af',
      texto: <><strong>Admin:</strong> vê todos os indicadores, cadastra usuários e indicadores, edita metas e resultados de todos.</>
    }
    if (role === 'coordenador') return {
      bg: 'var(--amber-bg)', border: 'var(--amber-border)', color: 'var(--amber)',
      texto: <><strong>Coordenador:</strong> vê apenas seus próprios indicadores, lança resultados mensais e preenche FCAs. Metas são somente leitura.</>
    }
    return {
      bg: 'var(--surface2)', border: 'var(--border)', color: 'var(--text2)',
      texto: <><strong>Gestor:</strong> vê seus indicadores e os dos coordenadores vinculados a ele pelo admin, lança resultados e preenche FCAs.</>
    }
  }

  return (
    <div>
      <div className="page-title">
        <div>
          <h2>Usuários</h2>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>
            Usuários com acesso à plataforma
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowForm(s => !s); setError(''); setNovoGestorCoords(new Set()) }}>
          {showForm ? 'Cancelar' : '+ Novo usuário'}
        </button>
      </div>

      {sucesso && (
        <div className="alert" style={{ background: 'var(--green-bg)', color: 'var(--green)', borderColor: 'var(--green)', borderLeft: `3px solid var(--green)`, marginBottom: 16 }}>
          {sucesso}
        </div>
      )}

      {/* Formulário de cadastro */}
      {showForm && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginBottom: 16 }}>Cadastrar novo usuário</h3>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleCreate}>
            <div className="row">
              <div className="col">
                <label>Nome completo *</label>
                <input
                  value={form.nome}
                  onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                  placeholder="Ex: João Silva"
                  required
                />
              </div>
              <div className="col">
                <label>Cargo</label>
                <input
                  value={form.cargo}
                  onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))}
                  placeholder="Ex: Gerente de Qualidade"
                />
              </div>
            </div>
            <div className="row" style={{ marginTop: 10 }}>
              <div className="col">
                <label>WhatsApp</label>
                <input
                  value={form.whatsapp}
                  onChange={e => setForm(f => ({ ...f, whatsapp: e.target.value }))}
                  placeholder="Ex: 11999998888"
                />
              </div>
            </div>
            <div className="row" style={{ marginTop: 10 }}>
              <div className="col">
                <label>E-mail *</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="email@empresa.com"
                  required
                />
              </div>
              <div className="col">
                <label>Senha inicial *</label>
                <input
                  type="password"
                  value={form.senha}
                  onChange={e => setForm(f => ({ ...f, senha: e.target.value }))}
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                />
              </div>
              <div className="col">
                <label>Perfil de acesso</label>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                  <option value="gestor">Gestor</option>
                  <option value="coordenador">Coordenador</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>

            {form.role === 'gestor' && coordenadoresList.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <label style={{ display: 'block', marginBottom: 8 }}>Coordenadores visíveis para este gestor</label>
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 8,
                  padding: '10px 14px', background: 'var(--surface2)',
                  border: '1px solid var(--border)', borderRadius: 8
                }}>
                  {coordenadoresList.map(c => (
                    <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                      <input
                        type="checkbox"
                        checked={novoGestorCoords.has(c.id)}
                        onChange={e => {
                          setNovoGestorCoords(prev => {
                            const next = new Set(prev)
                            if (e.target.checked) next.add(c.id)
                            else next.delete(c.id)
                            return next
                          })
                        }}
                        style={{ width: 16, height: 16, accentColor: 'var(--blue)' }}
                      />
                      <div>
                        <span style={{ fontWeight: 500 }}>{c.nome}</span>
                        {c.cargo && <span style={{ color: 'var(--text2)', fontSize: 12, marginLeft: 6 }}>{c.cargo}</span>}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {(() => {
              const info = roleInfoBox(form.role)
              return (
                <div style={{
                  marginTop: 14, padding: '10px 14px',
                  background: info.bg, border: `1px solid ${info.border}`,
                  borderRadius: 8, fontSize: 12, color: info.color
                }}>
                  {info.texto}
                </div>
              )
            })()}

            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Criando...' : 'Criar usuário'}
              </button>
              <button type="button" className="btn" onClick={() => { setShowForm(false); setNovoGestorCoords(new Set()) }}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal de edição */}
      {editando && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 480, width: '100%' }}>
            <h3 style={{ marginBottom: 16 }}>Editar usuário</h3>
            {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
            <form onSubmit={handleEditSave}>
              <div className="row">
                <div className="col">
                  <label>Nome completo *</label>
                  <input
                    value={editForm.nome}
                    onChange={e => setEditForm(f => ({ ...f, nome: e.target.value }))}
                    required
                  />
                </div>
                <div className="col">
                  <label>Cargo</label>
                  <input
                    value={editForm.cargo}
                    onChange={e => setEditForm(f => ({ ...f, cargo: e.target.value }))}
                    placeholder="Ex: Gerente de Qualidade"
                  />
                </div>
              </div>
              <div className="row" style={{ marginTop: 10 }}>
                <div className="col">
                  <label>WhatsApp</label>
                  <input
                    value={editForm.whatsapp}
                    onChange={e => setEditForm(f => ({ ...f, whatsapp: e.target.value }))}
                    placeholder="Ex: 11999998888"
                  />
                </div>
                <div className="col">
                  <label>Perfil de acesso</label>
                  <select value={editForm.role} onChange={e => setEditForm(f => ({ ...f, role: e.target.value }))}>
                    <option value="gestor">Gestor</option>
                    <option value="coordenador">Coordenador</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              {editForm.role === 'gestor' && coordenadoresList.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Coordenadores visíveis</label>
                  <div style={{
                    display: 'flex', flexDirection: 'column', gap: 8,
                    padding: '10px 14px', background: 'var(--surface2)',
                    border: '1px solid var(--border)', borderRadius: 8
                  }}>
                    {coordenadoresList.map(c => (
                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
                        <input
                          type="checkbox"
                          checked={editGestorCoords.has(c.id)}
                          onChange={e => {
                            setEditGestorCoords(prev => {
                              const next = new Set(prev)
                              if (e.target.checked) next.add(c.id)
                              else next.delete(c.id)
                              return next
                            })
                          }}
                          style={{ width: 16, height: 16, accentColor: 'var(--blue)' }}
                        />
                        <div>
                          <span style={{ fontWeight: 500 }}>{c.nome}</span>
                          {c.cargo && <span style={{ color: 'var(--text2)', fontSize: 12, marginLeft: 6 }}>{c.cargo}</span>}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
                <button type="button" className="btn" onClick={() => { setEditando(null); setError('') }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de exclusão */}
      {confirmandoExcluir && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 360, width: '100%', textAlign: 'center' }}>
            <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>Excluir usuário?</p>
            <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20 }}>
              <strong>{confirmandoExcluir.nome}</strong> será removido da plataforma. Esta ação não pode ser desfeita.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button className="btn btn-sm" style={{ background: 'var(--red)', color: '#fff', borderColor: 'var(--red)' }}
                onClick={() => handleDelete(confirmandoExcluir)}>Sim, excluir</button>
              <button className="btn btn-sm" onClick={() => setConfirmandoExcluir(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de alteração de senha */}
      {alterandoSenha && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: 360, width: '100%' }}>
            <h3 style={{ marginBottom: 4 }}>Alterar senha</h3>
            <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 16 }}>{alterandoSenha.nome}</p>
            {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
            <form onSubmit={handleAlterarSenha}>
              <label>Nova senha *</label>
              <input
                type="password"
                value={novaSenha}
                onChange={e => setNovaSenha(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
                autoFocus
              />
              <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar senha'}
                </button>
                <button type="button" className="btn" onClick={() => { setAlterandoSenha(null); setNovaSenha(''); setError('') }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lista de usuários */}
      {loading ? (
        <div className="empty-state">Carregando...</div>
      ) : usuarios.length === 0 ? (
        <div className="empty-state">Nenhum usuário cadastrado.</div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Cargo</th>
                <th>WhatsApp</th>
                <th>E-mail</th>
                <th>Perfil</th>
                <th>Indicadores / Coordenadores</th>
                <th>Cadastrado em</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {usuarios.map(u => {
                const inds = indicadoresPorGestor[u.id] || []
                const coordCount = vinculos.filter(v => v.gestor_id === u.id).length
                return (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.nome}</td>
                    <td style={{ color: 'var(--text2)' }}>{u.cargo || '—'}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>
                      {u.whatsapp
                        ? <a href={`https://wa.me/55${u.whatsapp.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" style={{ color: 'var(--green)' }}>{u.whatsapp}</a>
                        : '—'}
                    </td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>{u.email || '—'}</td>
                    <td>
                      <span className={`badge ${ROLE_BADGE[u.role] || 'badge-gray'}`}>
                        {ROLE_LABEL[u.role] || u.role}
                      </span>
                    </td>
                    <td>
                      {u.role === 'gestor' ? (
                        <span style={{ fontSize: 12, color: coordCount > 0 ? 'var(--text)' : 'var(--text3)' }}>
                          {coordCount > 0 ? `${coordCount} coordenador${coordCount > 1 ? 'es' : ''}` : 'nenhum coordenador'}
                        </span>
                      ) : inds.length === 0 ? (
                        <span style={{ color: 'var(--text3)', fontSize: 12 }}>nenhum</span>
                      ) : (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {inds.map(i => (
                            <span key={i.id} className="badge badge-gray" style={{ fontSize: 11 }}>{i.nome}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={{ color: 'var(--text2)', fontFamily: 'var(--mono)', fontSize: 12 }}>
                      {new Date(u.created_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-ghost" onClick={() => handleOpenEdit(u)}>
                          Editar
                        </button>
                        <button className="btn btn-sm btn-ghost" onClick={() => { setAlterandoSenha(u); setNovaSenha(''); setError('') }}>
                          Senha
                        </button>
                        <button
                          className="btn btn-sm"
                          style={{ color: 'var(--red)', borderColor: 'var(--red-border)' }}
                          onClick={() => setConfirmandoExcluir(u)}
                        >
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
